<script setup lang="ts">
/**
 * How deep the contents page lists, what stands under its title, and how each
 * level is set: a named text style of the document for each, so the charter
 * is changed in one place and every contents page follows.
 */
import { useI18n } from 'vue-i18n'

import { Select, Switch } from '@/components/ui'
import InspectorField from '@/sections/InspectorField.vue'
import type { ContentsSection } from '@/domain/section'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()
const report = useReportStore()

const props = defineProps<{ section: ContentsSection }>()
const emit = defineEmits<{ change: [section: ContentsSection] }>()

const DEPTHS = [1, 2, 3] as const

function set(changes: Partial<ContentsSection>): void {
  emit('change', { ...props.section, ...changes })
}

const LEVELS = [0, 1, 2] as const

/** One level's style changed, the others left as they were. */
function setLevelStyle(level: 0 | 1 | 2, id: string | undefined): void {
  const next: [string, string, string] = [...props.section.levelStyleIds]
  next[level] = id ?? ''
  set({ levelStyleIds: next })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.contents.title')">
      <input
        class="b-input"
        :value="section.title"
        @input="set({ title: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <InspectorField :label="t('inspector.contents.titleStyle')">
      <Select
        size="sm"
        :model-value="section.titleStyleId"
        @update:model-value="set({ titleStyleId: $event ?? '' })"
      >
        <option value="">{{ t('inspector.contents.levelStyleNone') }}</option>
        <option v-for="style in report.layout.textStyles" :key="style.id" :value="style.id">
          {{ style.name }}
        </option>
      </Select>
    </InspectorField>

    <InspectorField :label="t('inspector.contents.subtitle')">
      <input
        class="b-input"
        :value="section.subtitle"
        @input="set({ subtitle: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <InspectorField
      :label="t('inspector.contents.depth')"
      :hint="t('inspector.contents.depthHint')"
    >
      <div class="depths" role="group" :aria-label="t('inspector.contents.depth')">
        <button
          v-for="depth in DEPTHS"
          :key="depth"
          type="button"
          class="depths__option"
          :class="{ 'depths__option--on': section.depth === depth }"
          :aria-pressed="section.depth === depth"
          @click="set({ depth })"
        >
          {{ depth }}
        </button>
      </div>
    </InspectorField>

    <div class="switches">
      <Switch :model-value="section.pageNumbers" @update:model-value="set({ pageNumbers: $event })">
        {{ t('inspector.contents.pageNumbers') }}
      </Switch>
      <Switch :model-value="section.showDetail" @update:model-value="set({ showDetail: $event })">
        {{ t('inspector.contents.showDetail') }}
      </Switch>
    </div>

    <p class="note">{{ t('inspector.contents.levelStyleHint') }}</p>

    <InspectorField
      v-for="level in LEVELS"
      :key="level"
      :label="t('inspector.contents.levelStyle', { level: level + 1 })"
    >
      <Select
        size="sm"
        :model-value="section.levelStyleIds[level]"
        @update:model-value="setLevelStyle(level, $event)"
      >
        <option value="">{{ t('inspector.contents.levelStyleNone') }}</option>
        <option v-for="style in report.layout.textStyles" :key="style.id" :value="style.id">
          {{ style.name }}
        </option>
      </Select>
    </InspectorField>

    <p class="note">{{ t('inspector.contents.note') }}</p>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.switches {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.depths {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  padding: 2px;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.depths__option {
  height: 26px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.depths__option--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

.note {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}
</style>
